/**
 * Composant ToastHost - Hôte pour les notifications toast
 * 
 * Ce composant affiche les notifications toast (succès/erreur) à l'utilisateur.
 * Il s'abonne au système de toast global et affiche les notifications dans
 * un coin de l'écran avec auto-dismiss après un délai.
 * 
 * Structure:
 * - Abonnement au système de toast via toast.subscribe()
 * - Gestion des timers pour auto-dismiss
 * - Position configurable (coins de l'écran)
 * - Style adaptatif selon le type (succès/erreur)
 * - Bouton de fermeture manuelle
 */

import React, { useEffect, useRef, useState, useCallback } from "react";
import { toast } from "../services/apiClient";

// Durée d'affichage automatique des toasts (en millisecondes)
const AUTO_HIDE_MS = 3000;

// Styles de positionnement pour les différents coins de l'écran
const POS_STYLE = {
  "bottom-right": { bottom: 16, right: 16 },
  "bottom-left": { bottom: 16, left: 16 },
  "top-right": { top: 16, right: 16 },
  "top-left": { top: 16, left: 16 },
};

export default function ToastHost({ position = "bottom-left" }) {
  // État pour stocker les toasts actifs
  const [items, setItems] = useState([]);
  
  // Ref pour stocker les timers d'auto-dismiss (persiste entre re-renders)
  const timersRef = useRef(new Map());

  /**
   * S'abonne au système de toast au montage du composant
   * - Crée un nouvel toast à chaque événement toast
   * - Programme l'auto-dismiss après AUTO_HIDE_MS
   * - Nettoie les timers au démontage
   */
  useEffect(() => {
    const timers = timersRef.current;
    
    // S'abonner aux événements toast
    const unsubscribe = toast.subscribe((evt) => {
      // Générer un ID unique pour ce toast
      const id = `${Date.now()}_${Math.random().toString(36).slice(2, 7)}`;
      
      // Normaliser le type (error ou success par défaut)
      const type = evt?.type === "error" ? "error" : "success";
      
      // Convertir le message en string
      const message = String(evt?.message || "");
      
      // Ajouter le toast à la liste
      setItems((prev) => [...prev, { id, type, message }]);

      // Programmer l'auto-dismiss
      const t = setTimeout(() => {
        setItems((prev) => prev.filter((x) => x.id !== id));
        timers.delete(id);
      }, AUTO_HIDE_MS);
      timers.set(id, t);
    });
    
    // Cleanup: désabonnement et suppression des timers
    return () => {
      unsubscribe?.();
      timers.forEach((t) => clearTimeout(t));
      timers.clear();
    };
  }, []);

  /**
   * Ferme manuellement un toast avant son auto-dismiss
   * @param {string} id - ID du toast à fermer
   */
  const dismiss = useCallback((id) => {
    const t = timersRef.current.get(id);
    if (t) clearTimeout(t);
    timersRef.current.delete(id);
    setItems((prev) => prev.filter((x) => x.id !== id));
  }, []);

  // Ne rien afficher s'il n'y a pas de toasts
  if (!items.length) return null;

  // Récupérer le style de positionnement
  const anchorStyle = POS_STYLE[position] || POS_STYLE["bottom-left"];

  return (
    <div
      aria-live="polite"
      aria-atomic="true"
      style={{
        position: "fixed",
        zIndex: 9999,
        display: "flex",
        flexDirection: "column",
        gap: 10,
        pointerEvents: "none", // N'intercepte pas les clics en dehors des toasts
        ...anchorStyle,
      }}
    >
      {/* Afficher chaque toast */}
      {items.map((it) => (
        <div
          key={it.id}
          role="status"
          style={{
            pointerEvents: "auto", // On peut cliquer pour fermer
            width: "min(420px, calc(100vw - 24px))",
            minWidth: 0,
            maxWidth: "calc(100vw - 24px)",
            padding: "10px 12px",
            borderRadius: 10,
            boxShadow: "var(--shadow-md)",
            border:
              it.type === "error"
                ? "1px solid var(--error)"
                : "1px solid var(--success)",
            background:
              it.type === "error"
                ? "var(--error-bg)"
                : "var(--success-bg)",
            color: it.type === "error" ? "var(--error)" : "var(--success)",
            display: "flex",
            alignItems: "center",
            gap: 10,
            fontSize: 14,
            fontWeight: 600,
          }}
        >
          {/* Indicateur visuel du type (cercle coloré) */}
          <span
            aria-hidden
            style={{
              width: 8,
              height: 8,
              borderRadius: 999,
              background: it.type === "error" ? "var(--error)" : "var(--success)",
              flexShrink: 0,
            }}
          />
          
          {/* Message du toast */}
          <div style={{ flex: 1 }}>{it.message}</div>
          
          {/* Bouton de fermeture manuelle */}
          <button
            onClick={() => dismiss(it.id)}
            aria-label="Fermer la notification"
            style={{
              background: "transparent",
              border: "none",
              color: "inherit",
              fontSize: 18,
              lineHeight: 1,
              cursor: "pointer",
              padding: 4,
            }}
            title="Fermer"
          >
            ×
          </button>
        </div>
      ))}
    </div>
  );
}


